import { formatTokenCount } from './TokenPacks'

const accountChanged = () => new Error('The IntelliKey account changed. Please try again.')

export const waitForStripeFulfillment = async ({ check, assertCurrent, signal, timeoutMs = 60000, intervalMs = 2000 }) => {
  const controller = new AbortController()
  const abort = () => controller.abort()
  const timeout = setTimeout(abort, timeoutMs)
  signal?.addEventListener('abort', abort, { once: true })
  if (signal?.aborted) abort()
  try {
    while (!controller.signal.aborted) {
      assertCurrent()
      const result = await check(controller.signal)
      assertCurrent()
      if (result?.fulfilled === true) return result
      await new Promise((resolve, reject) => {
        const cancel = () => { clearTimeout(timer); reject(new Error('Payment confirmation is pending. Do not buy again.')) }
        const timer = setTimeout(() => { controller.signal.removeEventListener('abort', cancel); resolve() }, intervalMs)
        controller.signal.addEventListener('abort', cancel, { once: true })
        if (controller.signal.aborted) cancel()
      })
    }
    throw new Error('Payment confirmation is pending. Do not buy again.')
  } finally {
    clearTimeout(timeout)
    signal?.removeEventListener('abort', abort)
    controller.abort()
  }
}

export const availableApplePacks = (catalog, storeProducts) => {
  const byId = new Map((storeProducts || []).map(product => [product.productId, product]))
  return (catalog || []).flatMap(pack => {
    const product = byId.get(pack.productId)
    if (!product || !product.displayName || !product.displayPrice ||
        !Number.isFinite(product.price) || product.price <= 0 || !(pack.tokenCount > 0)) return []
    return [{ ...pack, provider: 'apple', name: product.displayName,
      displayPrice: product.displayPrice, price: product.price }]
  })
}

/** Payment orchestration is separate from rendering and legacy native shells. */
export class TokenPurchaseClient {
  constructor ({ api, nativeCall, getSession, isCurrent, hasApplePurchases, isNative, synchronizeIdentity, prepareBilling = async () => {} }) {
    Object.assign(this, { api, nativeCall, getSession, isCurrent, hasApplePurchases, isNative, synchronizeIdentity, prepareBilling })
    this.verifying = new Map()
    this.purchaseActive = false
  }

  assertCurrent = session => {
    if (!session?.authUID || !this.isCurrent(session)) throw accountChanged()
  }

  appleContext = async session => {
    this.assertCurrent(session)
    if (!this.hasApplePurchases()) throw new Error('Update IntelliKey to purchase Token Packs on this device.')
    await this.synchronizeIdentity()
    this.assertCurrent(session)
    const catalog = await this.api('catalog', { provider: 'apple' })
    this.assertCurrent(session)
    if (!catalog.appAccountToken || !Array.isArray(catalog.products)) throw new Error('Token Packs are unavailable. Please try again.')
    const configured = await this.nativeCall('tokenPurchases', {
      operation: 'configure', appAccountToken: catalog.appAccountToken
    }, 30000)
    this.assertCurrent(session)
    if (configured?.ok !== true) throw new Error('Could not connect purchases to your IntelliKey account. Please try again.')
    return catalog
  }

  load = async () => {
    const session = this.getSession()
    this.assertCurrent(session)
    if (this.hasApplePurchases()) {
      const catalog = await this.appleContext(session)
      const result = await this.nativeCall('tokenPurchases', {
        operation: 'products', productIds: catalog.products.map(pack => pack.productId)
      }, 30000)
      this.assertCurrent(session)
      if (result?.ok !== true) throw new Error('Could not load purchases from Apple. Please try again.')
      return { provider: 'apple', products: availableApplePacks(catalog.products, result.products) }
    }
    if (this.isNative()) return { provider: 'unavailable', products: [], message: 'Token Packs are not available in this version of the app.' }
    const catalog = await this.api('catalog', { provider: 'stripe' })
    this.assertCurrent(session)
    const products = catalog.enabled && catalog.publishableKey && Array.isArray(catalog.products)
      ? catalog.products.filter(pack => (!pack.isDraft || catalog.sandbox === true) && pack.price > 0 && pack.tokenCount > 0).map(pack => ({
        ...pack, provider: 'stripe', name: `${formatTokenCount(pack.tokenCount)} Tokens`,
        displayPrice: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(pack.price)
      })) : []
    return { ...catalog, provider: 'stripe', products,
      message: catalog.enabled ? catalog.message : 'Website payments are not available yet. Please try again later.' }
  }

  resume = async () => {
    if (!this.hasApplePurchases()) return
    const session = this.getSession()
    await this.appleContext(session)
    this.assertCurrent(session)
    await this.nativeCall('tokenPurchases', { operation: 'unfinished' }, 30000)
  }

  verify = async (transaction, session = this.getSession()) => {
    this.assertCurrent(session)
    if (!transaction?.transactionId || !transaction.jws || !transaction.productId || transaction.authUID !== session.authUID) {
      throw new Error('The purchase could not be verified for this account.')
    }
    const key = `${session.authGeneration}:${session.authUID}:${transaction.transactionId}`
    if (this.verifying.has(key)) return this.verifying.get(key)
    const work = (async () => {
      const result = await this.api('apple/verify', {
        transactionId: transaction.transactionId, productId: transaction.productId, receiptData: transaction.jws
      })
      this.assertCurrent(session)
      if (result?.verified !== true || !['Production', 'Sandbox'].includes(result.environment) ||
          result.sandbox !== (result.environment === 'Sandbox') || result.credited !== !result.sandbox) {
        throw new Error('Purchase verification is pending. Your purchase will be retried; do not buy it again.')
      }
      // A first iOS 15 sandbox install may receive its app receipt only after
      // purchase. Configure billing here, without blocking product discovery.
      try {
        const billing = await this.prepareBilling()
        this.assertCurrent(session)
        // Only a committed server ledger acknowledgement permits finishing.
        const finished = await this.nativeCall('tokenPurchases', {
          operation: 'finish', transactionId: transaction.transactionId
        }, 30000)
        this.assertCurrent(session)
        return { ...result, sandboxCredited: result.sandboxCredited === true && billing?.environment === 'Sandbox', finishPending: finished?.ok !== true }
      } catch (error) {
        this.assertCurrent(session)
        return { ...result, sandboxCredited: false, finishPending: true }
      }
    })()
    this.verifying.set(key, work)
    try { return await work } finally { this.verifying.delete(key) }
  }

  purchase = async (pack, onStatus = () => {}) => {
    if (this.purchaseActive) return false
    const session = this.getSession()
    this.assertCurrent(session)
    if (!this.hasApplePurchases()) {
      onStatus('failed', 'Purchases are unavailable in this app version.')
      return false
    }
    this.purchaseActive = true
    const status = (value, message) => { if (this.isCurrent(session)) onStatus(value, message) }
    let purchased = false
    let awaitingApple = false
    try {
      status('purchase', 'Contacting Apple')
      // Recheck availability immediately before opening the payment sheet.
      const catalog = await this.load()
      this.assertCurrent(session)
      if (!catalog.products.some(product => product.productId === pack.productId)) throw new Error('This Token Pack is not currently available from Apple.')
      awaitingApple = true
      const result = await this.nativeCall('tokenPurchases', {
        operation: 'purchase', productId: pack.productId
      }, 10 * 60 * 1000)
      this.assertCurrent(session)
      if (result?.status === 'cancelled') { status('canceled', 'Payment cancelled'); return false }
      if (result?.status === 'pending') { status('pending', 'Waiting for Apple approval. Tokens will be added after verification.'); return false }
      if (result?.ok !== true || result.status !== 'purchased') {
        awaitingApple = !['product-unavailable', 'invalid-product', 'purchase-in-progress'].includes(result?.error)
        throw new Error('Apple could not complete the purchase. Please try again.')
      }
      purchased = true
      status('verify', 'Verifying Apple purchase')
      const verified = await this.verify(result, session)
      status(verified.sandbox && !verified.sandboxCredited ? 'sandbox' : 'complete', verified.sandbox && !verified.sandboxCredited
        ? 'Purchase verification is pending.' : 'Tokens added')
      return verified
    } catch (error) {
      status(purchased || awaitingApple ? 'verification-pending' : 'failed', purchased || awaitingApple
        ? 'Purchase verification is pending. Reopen Token Packs to retry; do not buy again.' : error.message)
      return false
    } finally { this.purchaseActive = false }
  }
}
